import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSurahList, useSurah } from '@/hooks/useSurah';
import { useAyahsBySurah } from '@/hooks/useAyah';
import { useAyahAudio } from '@/hooks/useAyahAudio';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSession } from '@/hooks/useSession';
import {
  useBulkMarkAyahRange,
  useReadsForSurah,
  useResetSurahProgress,
  useToggleAyahRead,
} from '@/hooks/useAyahReads';
import { CompletionModal } from '@/components/reader/CompletionModal';
import { AlreadyCompletedModal } from '@/components/reader/AlreadyCompletedModal';
import { ResetSurahDialog } from '@/components/reader/ResetSurahDialog';
import { ShortcutsOverlay } from '@/components/reader/ShortcutsOverlay';
import { HighlightAllahText } from '@/components/ui/HighlightAllahText';
import {
  getGlobalAyahNumber,
  getMisharyAudioUrl,
} from '@/lib/utils/quranAyahNumbering';
import { revelationLabel } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';
import { canBulkMarkAyahs } from '@/lib/auth/admins';
import { isDuaAyah } from '@/lib/quran/duaAyahs';
import {
  IconBookmark,
  IconCheckCircle,
  IconChevronRight,
  IconCopy,
  IconForward10,
  IconMic,
  IconMore,
  IconPause,
  IconPlay,
  IconReplay10,
  IconShare,
  IconSparkle,
  IconVolume,
} from '@/components/home/icons';
import type { Surah } from '@/types/domain';

type LangFilter = 'all' | 'en' | 'ur';
type AudioApi = ReturnType<typeof useAyahAudio>;

const SAJDAH_AYAHS = new Set([
  '7:206',
  '13:15',
  '16:50',
  '17:109',
  '19:58',
  '22:18',
  '22:77',
  '25:60',
  '27:26',
  '32:15',
  '38:24',
  '41:38',
  '53:62',
  '84:21',
  '96:19',
]);

/**
 * Reader v2 — dashboard-style single-ayah reader.
 *
 * Adapted from the Stitch "Noor Intelligence" design. Three-column horizon:
 *   • Left: study-progress + reading-consistency cards (static placeholders
 *     for now — the app has no points/streak data).
 *   • Middle: the ayah itself — Arabic + English (Sahih International) +
 *     Urdu (Jalandhari), with Copy / Share / Mark-as-read.
 *   • Right: tafsir summary + topic chips drawn from the surah metadata.
 *
 * Floating audio player at the bottom plays Mishary Rashid Alafasy's
 * recitation from the public Islamic Network CDN (same source as /memorize),
 * with seek and 10-second skip.
 *
 * URL state mirrors /reader: `?surah=N&ayah=M`. Defaults to Al-Fatihah
 * 1:1 when no params are supplied — clicking "Reader" in the nav lands
 * users at the start of the Quran, not at a leftover design verse.
 */
export function Reader() {
  // --- URL state ------------------------------------------------------
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSurah = Number(searchParams.get('surah') ?? '1');
  const rawAyah = Number(searchParams.get('ayah') ?? '1');
  const surahId = clamp(Number.isFinite(rawSurah) ? rawSurah : 1, 1, 114);
  const ayahNumber = Number.isFinite(rawAyah) && rawAyah > 0 ? rawAyah : 1;

  // --- Data -----------------------------------------------------------
  const surahListQuery = useSurahList();
  const currentSurahQuery = useSurah(surahId);
  const ayahsQuery = useAyahsBySurah(surahId);

  // --- Derived --------------------------------------------------------
  const ayahCount = currentSurahQuery.data?.ayah_count ?? 0;
  const clampedAyahNumber =
    ayahCount > 0 ? Math.min(Math.max(ayahNumber, 1), ayahCount) : ayahNumber;
  const currentAyah = useMemo(
    () => ayahsQuery.data?.find((a) => a.ayah_number === clampedAyahNumber),
    [ayahsQuery.data, clampedAyahNumber],
  );
  const isFirstOverall = surahId === 1 && clampedAyahNumber === 1;
  const isLastOverall =
    surahId === 114 && ayahCount > 0 && clampedAyahNumber === ayahCount;
  const isSajdahAyah = SAJDAH_AYAHS.has(`${surahId}:${clampedAyahNumber}`);
  const isDuaHighlightedAyah = isDuaAyah(surahId, clampedAyahNumber);

  // --- Audio ----------------------------------------------------------
  const globalAyahNumber = useMemo(() => {
    if (!surahListQuery.data) return null;
    return getGlobalAyahNumber(
      surahId,
      clampedAyahNumber,
      surahListQuery.data,
    );
  }, [surahListQuery.data, surahId, clampedAyahNumber]);
  const audioUrl =
    globalAyahNumber !== null ? getMisharyAudioUrl(globalAyahNumber) : null;
  const audio = useAyahAudio({ audioUrl, initialRepeatMode: 'once' });

  // --- Local UI state -------------------------------------------------
  const [langFilter, setLangFilter] = useState<LangFilter>('all');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');

  useEffect(() => {
    setCopyStatus('idle');
  }, [surahId, clampedAyahNumber]);

  // --- Read marks (per-surah progress) --------------------------------
  // Drives the Study Progress card AND the Mark-as-read pill on the
  // ayah card. Source of truth lives in `ayah_reads` on the server;
  // here we just consume the cached Set and a toggle mutation.
  const session = useSession();
  const isSignedIn = !!session.data;
  const readsHook = useReadsForSurah(surahId);
  const { readSet, isSuccess: readsLoaded } = readsHook;
  const toggleRead = useToggleAyahRead(surahId);
  const bulkMarkRange = useBulkMarkAyahRange(surahId);
  const resetSurah = useResetSurahProgress(surahId);
  const isMarkedRead = readSet.has(clampedAyahNumber);
  const readCount = readSet.size;
  const isComplete = ayahCount > 0 && readCount >= ayahCount;
  const canUseBulkRange = canBulkMarkAyahs(session.data?.user.email);

  // --- Modal state ----------------------------------------------------
  // Completion modal opens on the upward 100% crossing (alongside the
  // confetti). Already-completed modal opens when the user *lands* on
  // a surah that's already 100% (calmer treatment, no confetti). Reset
  // dialog is opened by the "Start fresh" link in StudyProgressCard.
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showAlreadyCompletedModal, setShowAlreadyCompletedModal] =
    useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSajdahPrompt, setShowSajdahPrompt] = useState(false);

  // --- Confetti on 100% (the trophy moment) ---------------------------
  // Fires exactly once per upward 100% crossing inside this surah.
  // `prevPctRef` survives re-renders so we don't double-fire on remount.
  // We avoid firing during initial load (when prev is 0 and the user
  // hasn't done anything) by requiring the previous pct to be strictly
  // less than 100 AND the surah to actually have ayahs.
  const prevPctRef = useRef<number | null>(null);
  useEffect(() => {
    if (ayahCount === 0) {
      prevPctRef.current = null;
      return;
    }
    const pct = Math.floor((readCount / ayahCount) * 100);
    const prev = prevPctRef.current;
    if (prev !== null && prev < 100 && pct >= 100) {
      void fireTrophyConfetti();
      // Open the completion modal AFTER confetti starts so the user
      // sees the celebration before the choice screen lands. The 800ms
      // delay matches roughly the first burst — long enough to feel
      // earned, short enough not to feel unresponsive.
      window.setTimeout(() => setShowCompletionModal(true), 800);
    }
    prevPctRef.current = pct;
  }, [readCount, ayahCount]);

  // Reset confetti tracking + close any open modals when the surah
  // changes. Entering a fully-complete surah from cache must NOT re-
  // fire the celebration, and stale modal state from a previous surah
  // shouldn't persist.
  useEffect(() => {
    prevPctRef.current = null;
    alreadyCheckedRef.current = false;
    setShowCompletionModal(false);
    setShowAlreadyCompletedModal(false);
    setShowResetDialog(false);
    setShowShortcuts(false);
    setShowSajdahPrompt(false);
  }, [surahId]);

  const lastSajdahCueRef = useRef<string | null>(null);
  useEffect(() => {
    const ayahKey = `${surahId}:${clampedAyahNumber}`;
    if (!isSajdahAyah) {
      lastSajdahCueRef.current = null;
      setShowSajdahPrompt(false);
      return;
    }
    if (lastSajdahCueRef.current === ayahKey) return;
    lastSajdahCueRef.current = ayahKey;
    setShowSajdahPrompt(true);
    void fireSajdahConfetti();
  }, [isSajdahAyah, surahId, clampedAyahNumber]);

  useEffect(() => {
    if (!showSajdahPrompt) return;
    const close = window.setTimeout(() => {
      setShowSajdahPrompt(false);
      setSearchParams({
        surah: String(surahId),
        ayah: String(clampedAyahNumber),
      });
    }, 5000);
    return () => window.clearTimeout(close);
  }, [showSajdahPrompt, setSearchParams, surahId, clampedAyahNumber]);

  // --- "Already completed" detection ---------------------------------
  // Fires the AlreadyCompletedModal exactly once per surah visit, when
  // the user lands on a surah whose read marks are already at 100%.
  //
  // Why a ref guard? `readCount` changes (e.g. after a Redo wipes it
  // to 0) would otherwise re-trigger this effect. We only care about
  // the *initial* state after the read query first resolves for this
  // surah. The ref is reset in the surah-change effect above.
  //
  // We also seed `prevPctRef` to 100 so the confetti effect, which
  // also runs on this change, doesn't interpret "null → 100" as a
  // fresh crossing.
  const alreadyCheckedRef = useRef(false);
  useEffect(() => {
    if (alreadyCheckedRef.current) return;
    if (!isSignedIn) return;
    if (ayahCount === 0) return;
    if (!readsLoaded) return;

    alreadyCheckedRef.current = true;

    if (readCount >= ayahCount) {
      // Pre-seed the confetti tracker so it sees prev=100 next tick
      // and doesn't celebrate a "0 → 100" jump that didn't really happen.
      prevPctRef.current = 100;
      setShowAlreadyCompletedModal(true);
    }
  }, [isSignedIn, ayahCount, readsLoaded, readCount]);

  // --- Title ----------------------------------------------------------
  useDocumentTitle(
    currentSurahQuery.data
      ? `${currentSurahQuery.data.name_en} ${clampedAyahNumber} · Reader`
      : 'Reader',
  );

  // --- Navigation -----------------------------------------------------
  const goTo = useCallback(
    (s: number, a: number) => {
      setSearchParams({ surah: String(s), ayah: String(a) });
    },
    [setSearchParams],
  );
  const handlePrev = useCallback(() => {
    if (ayahCount === 0) return;
    if (isFirstOverall) return;
    if (clampedAyahNumber > 1) {
      goTo(surahId, clampedAyahNumber - 1);
      return;
    }
    const prev = surahListQuery.data?.find((s) => s.id === surahId - 1);
    if (prev) goTo(prev.id, prev.ayah_count);
  }, [ayahCount, isFirstOverall, clampedAyahNumber, surahId, surahListQuery.data, goTo]);
  const handleNext = useCallback(() => {
    if (ayahCount === 0) return;
    if (isLastOverall) return;
    if (ayahCount > 0 && clampedAyahNumber < ayahCount) {
      goTo(surahId, clampedAyahNumber + 1);
      return;
    }
    if (surahId < 114) goTo(surahId + 1, 1);
  }, [isLastOverall, ayahCount, clampedAyahNumber, surahId, goTo]);

  // --- Keyboard -------------------------------------------------------
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'SELECT' ||
        tag === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === ' ' || e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        if (audio.isPlaying) audio.pause();
        else audio.play();
      } else if ((e.key === 'r' || e.key === 'R') && e.shiftKey) {
        // Shift+R: toggle Mark as read for the current ayah. Same
        // gating as the button — needs sign-in and no in-flight write.
        e.preventDefault();
        if (isSignedIn && !toggleRead.isPending) {
          toggleRead.toggle(clampedAyahNumber, isMarkedRead);
        }
      } else if (e.key === '?') {
        // `?` (which is Shift+/ on US layouts) toggles the shortcuts
        // overlay. The browser may have already produced `?` here, so
        // we don't need to check for Shift explicitly.
        e.preventDefault();
        setShowShortcuts((v) => !v);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    audio,
    handleNext,
    handlePrev,
    isSignedIn,
    toggleRead,
    clampedAyahNumber,
    isMarkedRead,
  ]);

  // --- Actions --------------------------------------------------------
  const handleCopy = useCallback(async () => {
    if (!currentAyah || !currentSurahQuery.data) return;
    const ref = `Surah ${currentSurahQuery.data.name_en} ${surahId}:${clampedAyahNumber}`;
    const parts = [
      currentAyah.text_ar,
      currentAyah.translation_en ?? '',
      `— ${ref}`,
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(parts.join('\n\n'));
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 1800);
    } catch {
      /* user denied clipboard; silently no-op */
    }
  }, [currentAyah, currentSurahQuery.data, surahId, clampedAyahNumber]);

  const handleShare = useCallback(async () => {
    if (!currentAyah || !currentSurahQuery.data) return;
    const ref = `Surah ${currentSurahQuery.data.name_en} ${surahId}:${clampedAyahNumber}`;
    const text = `${currentAyah.translation_en ?? currentAyah.text_ar}\n\n— ${ref}`;
    const url = window.location.href;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: ref, text, url });
      } catch {
        /* user dismissed */
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        setCopyStatus('copied');
        setTimeout(() => setCopyStatus('idle'), 1800);
      } catch {
        /* ignore */
      }
    }
  }, [currentAyah, currentSurahQuery.data, surahId, clampedAyahNumber]);

  // --- Render ---------------------------------------------------------
  const isLoading = currentSurahQuery.isLoading || ayahsQuery.isLoading;
  const isError = currentSurahQuery.isError || ayahsQuery.isError;
  const showEn = langFilter === 'all' || langFilter === 'en';
  const showUr = langFilter === 'all' || langFilter === 'ur';

  // Topics for the right rail — drawn from the actual ayah / surah meta.
  const topics: string[] = [];
  if (currentSurahQuery.data?.revelation_type) {
    topics.push(revelationLabel(currentSurahQuery.data.revelation_type));
  }
  if (currentAyah?.juz) topics.push(`Juz ${currentAyah.juz}`);
  if (currentAyah?.page) topics.push(`Page ${currentAyah.page}`);

  return (
    <section className="mx-auto w-full max-w-7xl px-4 pb-12 pt-6 sm:px-6 lg:pb-32">
      <ContextBar
        surahId={surahId}
        ayahNumber={clampedAyahNumber}
        ayahCount={ayahCount}
        surahList={surahListQuery.data ?? []}
        onGoTo={goTo}
        langFilter={langFilter}
        onLangChange={setLangFilter}
      />
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start">
        {/* Left rail */}
        <aside className="space-y-6 lg:col-span-3">
          <StudyProgressCard
            surahName={currentSurahQuery.data?.name_en}
            readCount={readCount}
            totalAyahs={ayahCount}
            isSignedIn={isSignedIn}
            isComplete={isComplete}
            onResetRequest={() => setShowResetDialog(true)}
          />
          {canUseBulkRange && (
            <BulkReadRangeCard
              ayahCount={ayahCount}
              currentAyah={clampedAyahNumber}
              isPending={bulkMarkRange.isPending}
              error={bulkMarkRange.error}
              onSubmit={(startAyah, endAyah) => {
                bulkMarkRange.mutate({ startAyah, endAyah });
              }}
            />
          )}
          <ReadingConsistencyCard />
        </aside>

        {/* Middle column */}
        <section className="space-y-6 lg:col-span-6">
          <AyahCard
            surahName={currentSurahQuery.data?.name_en}
            surahId={surahId}
            ayahNumber={clampedAyahNumber}
            arabic={currentAyah?.text_ar ?? ''}
            translationEn={currentAyah?.translation_en ?? null}
            translationUr={currentAyah?.translation_ur ?? null}
            showEn={showEn}
            showUr={showUr}
            isLoading={isLoading}
            isError={isError}
            isMarkedRead={isMarkedRead}
            isToggleReadDisabled={!isSignedIn || toggleRead.isPending}
            copyStatus={copyStatus}
            onCopy={handleCopy}
            onShare={handleShare}
            onToggleRead={() =>
              toggleRead.toggle(clampedAyahNumber, isMarkedRead)
            }
            isSajdahAyah={isSajdahAyah}
            isDuaHighlightedAyah={isDuaHighlightedAyah}
          />
          <PrevNextNav
            isFirst={isFirstOverall}
            isLast={isLastOverall}
            prevLabel={
              clampedAyahNumber > 1
                ? `Verse ${clampedAyahNumber - 1}`
                : 'Previous surah'
            }
            nextLabel={
              ayahCount > 0 && clampedAyahNumber < ayahCount
                ? `Verse ${clampedAyahNumber + 1}`
                : 'Next surah'
            }
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </section>

        {/* Right rail */}
        <aside className="lg:col-span-3">
          <TafsirCard
            tafsir={currentAyah?.tafsir_en ?? null}
            isLoading={isLoading}
            topics={topics}
          />
        </aside>
      </div>

      <AudioPlayer
        audio={audio}
        surahName={currentSurahQuery.data?.name_en}
        ayahNumber={clampedAyahNumber}
      />

      {/* Surah-complete celebration. Fires alongside the confetti and
          offers Continue / Browse / Stay choices. */}
      <CompletionModal
        open={showCompletionModal}
        surahName={currentSurahQuery.data?.name_en}
        surahNumber={surahId}
        totalAyahs={ayahCount}
        onContinue={(nextSurahNumber) => {
          setShowCompletionModal(false);
          if (nextSurahNumber == null) {
            navigate('/surah');
          } else {
            // Land on ayah 1 of the next surah. setSearchParams keeps
            // us inside /reader so the modal's "Continue" feels in-place.
            setSearchParams({
              surah: String(nextSurahNumber),
              ayah: '1',
            });
          }
        }}
        onBackToReader={() => {
          setShowCompletionModal(false);
          // "Back to Reader" — restart at Al-Fatiha. setSearchParams
          // instead of navigate so we stay inside /reader (no full
          // route transition), preserving the audio + layout state.
          setSearchParams({ surah: '1', ayah: '1' });
        }}
        onClose={() => setShowCompletionModal(false)}
      />

      {/* "You've already completed this surah" — opens when the user
          revisits a 100%-marked surah. Calmer than CompletionModal
          (no confetti), with a Redo option that wipes this surah's
          progress and restarts at ayah 1. */}
      <AlreadyCompletedModal
        open={showAlreadyCompletedModal}
        surahName={currentSurahQuery.data?.name_en}
        surahNumber={surahId}
        totalAyahs={ayahCount}
        isResetting={resetSurah.isPending}
        onContinue={(nextSurahNumber) => {
          setShowAlreadyCompletedModal(false);
          if (nextSurahNumber == null) {
            navigate('/surah');
          } else {
            setSearchParams({
              surah: String(nextSurahNumber),
              ayah: '1',
            });
          }
        }}
        onRedo={() => {
          resetSurah.mutate(undefined, {
            onSuccess: () => {
              setShowAlreadyCompletedModal(false);
              // Reseat confetti tracker at 0 so the *next* upward
              // 100% crossing celebrates fresh.
              prevPctRef.current = 0;
              // Send the user back to the start of this surah.
              setSearchParams({
                surah: String(surahId),
                ayah: '1',
              });
            },
          });
        }}
        onClose={() => setShowAlreadyCompletedModal(false)}
      />

      {/* Destructive reset confirmation, triggered by the "Start fresh"
          link on the StudyProgressCard in Revision Mode. */}
      <ResetSurahDialog
        open={showResetDialog}
        surahName={currentSurahQuery.data?.name_en}
        isResetting={resetSurah.isPending}
        onConfirm={() => {
          resetSurah.mutate(undefined, {
            onSuccess: () => {
              setShowResetDialog(false);
              // After a reset, clear the confetti tracker so the next
              // 100% crossing celebrates fresh.
              prevPctRef.current = 0;
            },
          });
        }}
        onCancel={() => setShowResetDialog(false)}
      />

      {/* Keyboard shortcuts help, toggled by `?`. */}
      <ShortcutsOverlay
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />

      <SajdahPrompt
        open={showSajdahPrompt}
        onClose={() => {
          setShowSajdahPrompt(false);
          setSearchParams({
            surah: String(surahId),
            ayah: String(clampedAyahNumber),
          });
        }}
      />
    </section>
  );
}

/* ================================================================== *
 * Sub-components
 * ================================================================== */

const SELECT_CLS = cn(
  'h-10 px-4 rounded-lg',
  'bg-night-800 border border-night-500/40',
  'text-sm font-medium text-night-50',
  'focus:border-beam focus:outline-none focus:ring-1 focus:ring-beam/40',
  'cursor-pointer transition-colors hover:border-beam/50',
);

function ContextBar({
  surahId,
  ayahNumber,
  ayahCount,
  surahList,
  onGoTo,
  langFilter,
  onLangChange,
}: {
  surahId: number;
  ayahNumber: number;
  ayahCount: number;
  surahList: Surah[];
  onGoTo: (s: number, a: number) => void;
  langFilter: LangFilter;
  onLangChange: (v: LangFilter) => void;
}) {
  const tabs: { v: LangFilter; label: string }[] = [
    { v: 'all', label: 'All' },
    { v: 'ur', label: 'Urdu' },
    { v: 'en', label: 'English' },
  ];
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-night-500/30 bg-night-700/50 p-4 backdrop-blur-md">
      <div className="flex flex-wrap gap-3">
        <select
          value={surahId}
          onChange={(e) => {
            onGoTo(Number(e.target.value), 1);
            // Blur immediately so focus returns to document.body and
            // the Reader's keyboard shortcuts (←/→, Space, ⇧R, ?) fire
            // without the user having to click somewhere else first.
            e.target.blur();
          }}
          disabled={surahList.length === 0}
          className={SELECT_CLS}
          aria-label="Select surah"
        >
          {surahList.map((s) => (
            <option key={s.id} value={s.id}>
              {s.id}. {s.name_en}
            </option>
          ))}
        </select>
        <select
          value={ayahNumber}
          onChange={(e) => {
            onGoTo(surahId, Number(e.target.value));
            e.target.blur();
          }}
          disabled={ayahCount === 0}
          className={SELECT_CLS}
          aria-label="Select ayah"
        >
          {ayahCount > 0 ? (
            Array.from({ length: ayahCount }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                Verse {i + 1}
              </option>
            ))
          ) : (
            <option value={ayahNumber}>—</option>
          )}
        </select>
      </div>
      <div className="flex rounded-lg border border-night-500/30 bg-night-800 p-1">
        {tabs.map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => onLangChange(t.v)}
            className={cn(
              'rounded-md px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors',
              langFilter === t.v
                ? 'bg-beam/15 text-beam'
                : 'text-night-200 hover:text-night-50',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---- Left rail ----------------------------------------------------- */

/**
 * Per-surah study progress. Driven entirely by `ayah_reads` — every
 * tick on the ring is a real "Mark as read" the user did. Reaching
 * 100% in the parent component fires the trophy confetti.
 *
 * Layout, top to bottom:
 *   • Title row: "STUDY PROGRESS" + disabled "Full Analytics" link
 *     (tooltip: "Coming soon" until the student dashboard exists).
 *   • Hero row: progress ring + surah name + "X of Y ayahs read".
 *   • Milestone row: 4 icons — ¼, ½, ¾, trophy. Active milestones
 *     glow cyan; inactive are dimmed.
 *   • Signed-out state: card dims and shows a Sign-in CTA in place
 *     of the hero row.
 */
function StudyProgressCard({
  surahName,
  readCount,
  totalAyahs,
  isSignedIn,
  isComplete,
  onResetRequest,
}: {
  surahName: string | undefined;
  readCount: number;
  totalAyahs: number;
  isSignedIn: boolean;
  /** True when readCount === totalAyahs > 0. Used to switch the card
   *  into Revision Mode (no reset of the trophy / milestones on revisit). */
  isComplete: boolean;
  /** Opens the parent's reset confirmation dialog. */
  onResetRequest: () => void;
}) {
  const RADIUS = 34;
  const STROKE = 8;
  const CIRC = 2 * Math.PI * RADIUS;

  const pct =
    totalAyahs > 0 ? Math.min(100, Math.floor((readCount / totalAyahs) * 100)) : 0;
  const DASH_OFFSET = CIRC * (1 - pct / 100);

  // Milestone thresholds — each icon lights up once `pct` ≥ its threshold.
  const milestones: { threshold: number; Icon: typeof MilestoneQuarter; label: string }[] = [
    { threshold: 25, Icon: MilestoneQuarter, label: '25%' },
    { threshold: 50, Icon: MilestoneHalf, label: '50%' },
    { threshold: 75, Icon: MilestoneThreeQuarter, label: '75%' },
    { threshold: 100, Icon: MilestoneTrophy, label: '100%' },
  ];

  return (
    <div className="group relative overflow-hidden rounded-3xl border border-night-500/30 bg-night-700/60 p-6">
      <div
        aria-hidden="true"
        className="absolute -bottom-4 -right-4 h-32 w-32 rounded-full bg-beam/5 blur-3xl transition-transform duration-700 group-hover:scale-150"
      />

      {/* Title row */}
      <div className="mb-6 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-night-100">
          Study Progress
        </h4>
        <button
          type="button"
          disabled
          title="Full analytics coming soon"
          className="cursor-not-allowed text-xs font-semibold text-night-300 opacity-60"
        >
          Full Analytics
        </button>
      </div>

      {isSignedIn ? (
        <div className="flex items-center gap-5">
          {/* Progress ring */}
          <div className="relative flex h-20 w-20 items-center justify-center">
            <svg className="-rotate-90" width="80" height="80" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r={RADIUS}
                fill="none"
                stroke="#2E3447"
                strokeWidth={STROKE}
              />
              <circle
                cx="40"
                cy="40"
                r={RADIUS}
                fill="none"
                stroke="#00E5FF"
                strokeWidth={STROKE}
                strokeDasharray={CIRC}
                strokeDashoffset={DASH_OFFSET}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 600ms ease-out' }}
              />
            </svg>
            <span className="absolute text-sm font-semibold text-beam">
              {pct}%
            </span>
          </div>

          {/* Surah name + read count */}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p
                className="truncate font-display text-lg font-extrabold text-night-50"
                title={surahName}
              >
                {surahName ?? '…'}
              </p>
              {isComplete && (
                <span className="rounded-md border border-beam/30 bg-beam/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-beam">
                  Complete
                </span>
              )}
            </div>
            <p className="text-[10px] uppercase tracking-wide text-night-300">
              {totalAyahs === 0
                ? 'Loading…'
                : isComplete
                  ? 'Revising — progress kept'
                  : `${readCount} of ${totalAyahs} ayahs read`}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4 rounded-2xl border border-night-500/30 bg-night-800/40 p-4">
          <p className="text-xs leading-relaxed text-night-200">
            Sign in to track your progress through each surah.
          </p>
          <a
            href="/login?next=/reader"
            className="rounded-lg bg-beam/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-beam transition-colors hover:bg-beam/30"
          >
            Sign in
          </a>
        </div>
      )}

      {/* Milestones */}
      <div className="mt-6 flex items-center justify-between border-t border-night-500/20 pt-6">
        {milestones.map(({ threshold, Icon, label }) => {
          const active = isSignedIn && pct >= threshold;
          return (
            <div
              key={threshold}
              className="flex flex-col items-center gap-1.5"
              title={`${label} milestone${active ? ' — reached' : ''}`}
            >
              <Icon
                className={cn(
                  'h-7 w-7 transition-colors duration-500',
                  active
                    ? threshold === 100
                      ? 'text-beam drop-shadow-[0_0_8px_rgba(0,229,255,0.45)]'
                      : 'text-beam'
                    : 'text-night-500/50',
                )}
              />
              <span
                className={cn(
                  'text-[9px] font-semibold uppercase tracking-wide transition-colors',
                  active ? 'text-beam/80' : 'text-night-300/60',
                )}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Revision-Mode reset link. Only appears when the surah is already
          complete, so we don't dangle a destructive action in front of
          users who are still working their way through. */}
      {isSignedIn && isComplete && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onResetRequest}
            className="text-[10px] font-medium uppercase tracking-wide text-night-300 underline decoration-night-500/40 underline-offset-4 transition-colors hover:text-red-300 hover:decoration-red-400/40"
          >
            Start fresh — reset this surah's progress
          </button>
        </div>
      )}
    </div>
  );
}

/* ---- Milestone icons (local — Reader-specific) -------------------- */

type MilestoneIconProps = { className?: string };

/** ¼-filled circle — first quarter milestone. */
function MilestoneQuarter({ className }: MilestoneIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
    >
      <circle cx="12" cy="12" r="9" />
      {/* 90° pie slice — from 12 o'clock to 3 o'clock */}
      <path d="M12 3 A9 9 0 0 1 21 12 L12 12 Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Half-filled circle — second quarter milestone. */
function MilestoneHalf({ className }: MilestoneIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
    >
      <circle cx="12" cy="12" r="9" />
      {/* 180° — right half */}
      <path d="M12 3 A9 9 0 0 1 12 21 Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** ¾-filled circle — third quarter milestone. */
function MilestoneThreeQuarter({ className }: MilestoneIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
    >
      <circle cx="12" cy="12" r="9" />
      {/* 270° pie slice — from 12 o'clock all the way around to 9 o'clock */}
      <path
        d="M12 3 A9 9 0 1 1 3 12 L12 12 Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

/** Trophy — surah complete. */
function MilestoneTrophy({ className }: MilestoneIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Cup */}
      <path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" />
      {/* Handles */}
      <path d="M7 6H5a2 2 0 0 0 0 4h2" />
      <path d="M17 6h2a2 2 0 0 1 0 4h-2" />
      {/* Stem + base */}
      <path d="M12 13v3" />
      <path d="M9 19h6" />
      <path d="M10 16h4l-1 3h-2l-1-3Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Fires a 2-second confetti burst from the bottom corners. Loaded
 * lazily so the (small) library doesn't bloat the initial bundle.
 * Brand-aligned palette — beam cyan plus the amber accent.
 */
async function fireTrophyConfetti() {
  if (typeof window === 'undefined') return;
  // Respect reduced-motion users — no confetti for them.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  try {
    const confetti = (await import('canvas-confetti')).default;
    const end = Date.now() + 2000;
    const colors = ['#00E5FF', '#67e8f9', '#22d3ee', '#fbbf24'];
    (function frame() {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 1 },
        colors,
        gravity: 0.9,
        scalar: 0.9,
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 1 },
        colors,
        gravity: 0.9,
        scalar: 0.9,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  } catch {
    /* swallow — confetti is decoration, never block reading */
  }
}

async function fireSajdahConfetti() {
  if (typeof window === 'undefined') return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  try {
    const confetti = (await import('canvas-confetti')).default;
    confetti({
      particleCount: 12,
      spread: 34,
      startVelocity: 10,
      scalar: 0.58,
      gravity: 0.48,
      drift: 0.1,
      ticks: 150,
      origin: { x: 0.5, y: 0.58 },
      colors: ['#00E5FF', '#67e8f9', '#fbbf24'],
    });
  } catch {
    /* decorative only */
  }
}

function ReadingConsistencyCard() {
  const bars = [40, 60, 30, 80, 95, 50, 45];
  return (
    <div className="rounded-3xl border border-night-500/20 bg-night-800/50 p-6">
      <p className="mb-4 text-[10px] uppercase tracking-wide text-night-300">
        Reading Consistency
      </p>
      <div className="flex h-16 items-end gap-1.5">
        {bars.map((h, i) => (
          <div
            key={i}
            style={{ height: `${h}%` }}
            className={cn(
              'flex-1 rounded-t-sm',
              i === 4
                ? 'bg-beam'
                : i === 3
                  ? 'bg-beam/60'
                  : 'bg-night-500/40',
            )}
          />
        ))}
      </div>
      <div className="mt-3 flex justify-between text-[10px] uppercase tracking-wide text-night-300">
        <span>Mon</span>
        <span>Sun</span>
      </div>
    </div>
  );
}

function BulkReadRangeCard({
  ayahCount,
  currentAyah,
  isPending,
  error,
  onSubmit,
}: {
  ayahCount: number;
  currentAyah: number;
  isPending: boolean;
  error: Error | null;
  onSubmit: (startAyah: number, endAyah: number) => void;
}) {
  const [startAyah, setStartAyah] = useState<number>(currentAyah);
  const [endAyah, setEndAyah] = useState<number>(currentAyah);
  const [lastAppliedRange, setLastAppliedRange] = useState<{
    startAyah: number;
    endAyah: number;
  } | null>(null);
  const [didManuallyEditRange, setDidManuallyEditRange] = useState(false);

  useEffect(() => {
    setStartAyah((prev) => clamp(prev, 1, Math.max(ayahCount, 1)));
    setEndAyah((prev) => clamp(prev, 1, Math.max(ayahCount, 1)));
  }, [ayahCount]);

  useEffect(() => {
    if (didManuallyEditRange) return;
    setStartAyah(currentAyah);
    setEndAyah(currentAyah);
  }, [currentAyah, didManuallyEditRange]);

  useEffect(() => {
    if (!isPending && !error && lastAppliedRange) {
      const clear = window.setTimeout(() => setLastAppliedRange(null), 2400);
      return () => window.clearTimeout(clear);
    }
  }, [isPending, error, lastAppliedRange]);

  const hasValidRange =
    ayahCount > 0 &&
    startAyah >= 1 &&
    endAyah >= startAyah &&
    endAyah <= ayahCount;
  const selectedCount = hasValidRange ? endAyah - startAyah + 1 : 0;
  const validationMessage =
    ayahCount === 0
      ? 'This surah does not have ayahs available to mark yet.'
      : startAyah < 1 || endAyah < 1
        ? 'Start and end must both be 1 or higher.'
        : startAyah > ayahCount || endAyah > ayahCount
          ? `Choose ayahs between 1 and ${ayahCount}.`
          : endAyah < startAyah
            ? 'End ayah must be the same as or after the start ayah.'
            : `${selectedCount} ayah${selectedCount === 1 ? '' : 's'} selected`;

  function applyPreset(nextStart: number, nextEnd: number) {
    setDidManuallyEditRange(true);
    setStartAyah(clamp(nextStart, 1, Math.max(ayahCount, 1)));
    setEndAyah(clamp(nextEnd, 1, Math.max(ayahCount, 1)));
  }

  function handleSubmit() {
    if (!hasValidRange) return;
    setLastAppliedRange({ startAyah, endAyah });
    setDidManuallyEditRange(false);
    onSubmit(startAyah, endAyah);
  }

  return (
    <div className="rounded-3xl border border-amber-400/20 bg-amber-300/5 p-6">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-200">
          Bulk Mark
        </p>
        <p className="mt-2 text-sm leading-relaxed text-night-200">
          Mark a continuous ayah range as read in one action.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => applyPreset(1, currentAyah)}
          disabled={ayahCount === 0}
          className="rounded-full border border-night-500/40 bg-night-800 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-night-100 transition hover:border-amber-300/40 hover:text-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          1 to current
        </button>
        <button
          type="button"
          onClick={() => applyPreset(currentAyah, ayahCount)}
          disabled={ayahCount === 0}
          className="rounded-full border border-night-500/40 bg-night-800 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-night-100 transition hover:border-amber-300/40 hover:text-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Current to end
        </button>
        <button
          type="button"
          onClick={() => applyPreset(1, ayahCount)}
          disabled={ayahCount === 0}
          className="rounded-full border border-night-500/40 bg-night-800 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-night-100 transition hover:border-amber-300/40 hover:text-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Whole surah
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs font-medium text-night-200">
          Start
          <input
            type="number"
            min={1}
            max={Math.max(ayahCount, 1)}
            value={startAyah}
            onChange={(e) => {
              setDidManuallyEditRange(true);
              setStartAyah(Number(e.target.value));
            }}
            className="mt-2 w-full rounded-xl border border-night-500/40 bg-night-800 px-3 py-2 text-sm text-night-50 focus:border-amber-300 focus:outline-none"
          />
        </label>
        <label className="text-xs font-medium text-night-200">
          End
          <input
            type="number"
            min={1}
            max={Math.max(ayahCount, 1)}
            value={endAyah}
            onChange={(e) => {
              setDidManuallyEditRange(true);
              setEndAyah(Number(e.target.value));
            }}
            className="mt-2 w-full rounded-xl border border-night-500/40 bg-night-800 px-3 py-2 text-sm text-night-50 focus:border-amber-300 focus:outline-none"
          />
        </label>
      </div>

      <p className="mt-3 text-[11px] uppercase tracking-wide text-night-300">
        {validationMessage}
      </p>

      {error && (
        <p
          role="alert"
          className="mt-3 rounded-xl border border-red-400/20 bg-red-400/10 px-3 py-2 text-sm text-red-100"
        >
          Could not mark ayahs {startAyah} to {endAyah}. Please try again.
        </p>
      )}

      {!error && lastAppliedRange && !isPending && (
        <p
          role="status"
          className="mt-3 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-100"
        >
          Marked ayahs {lastAppliedRange.startAyah} to {lastAppliedRange.endAyah} as read.
        </p>
      )}

      <button
        type="button"
        disabled={!hasValidRange || isPending}
        onClick={handleSubmit}
        className="mt-4 w-full rounded-xl bg-amber-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-amber-950 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isPending ? 'Marking…' : 'Mark Range as Read'}
      </button>
    </div>
  );
}

/* ---- Middle column ------------------------------------------------- */

function AyahCard({
  surahName,
  surahId,
  ayahNumber,
  arabic,
  translationEn,
  translationUr,
  showEn,
  showUr,
  isLoading,
  isError,
  isMarkedRead,
  isToggleReadDisabled,
  copyStatus,
  onCopy,
  onShare,
  onToggleRead,
  isSajdahAyah,
  isDuaHighlightedAyah,
}: {
  surahName: string | undefined;
  surahId: number;
  ayahNumber: number;
  arabic: string;
  translationEn: string | null;
  translationUr: string | null;
  showEn: boolean;
  showUr: boolean;
  isLoading: boolean;
  isError: boolean;
  isMarkedRead: boolean;
  isToggleReadDisabled: boolean;
  copyStatus: 'idle' | 'copied';
  onCopy: () => void;
  onShare: () => void;
  onToggleRead: () => void;
  isSajdahAyah: boolean;
  isDuaHighlightedAyah: boolean;
}) {
  return (
    <article className="relative overflow-hidden rounded-3xl border border-night-500/20 bg-night-950/60 p-6 transition-colors hover:border-beam/30 md:p-10">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-beam to-transparent opacity-30"
      />

      <div className="mb-8 flex items-start justify-between">
        <span className="rounded-full border border-beam/30 bg-beam/10 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-beam">
          {surahName ?? '…'} {surahId}:{ayahNumber}
        </span>
        <div className="flex gap-3 text-night-200">
          <button
            type="button"
            className="transition-colors hover:text-beam"
            aria-label="Bookmark this ayah"
          >
            <IconBookmark className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="transition-colors hover:text-beam"
            aria-label="More options"
          >
            <IconMore className="h-5 w-5" />
          </button>
        </div>
      </div>

      {isError ? (
        <p className="text-night-200">Could not load this ayah.</p>
      ) : isLoading || !arabic ? (
        <AyahSkeleton />
      ) : (
        <>
          <div className="mb-10 text-right">
            <p
              dir="rtl"
              className="reader-arabic-display"
            >
              <span className={cn(isDuaHighlightedAyah && 'dua-highlight')}>
                {arabic}
              </span>
            </p>
            {isSajdahAyah && (
              <div className="mt-5 flex justify-end">
                <SajdahInlinePill />
              </div>
            )}
          </div>

          <div className="space-y-6 border-t border-night-500/15 pt-7">
            {showEn && translationEn && (
              <div>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-beam/70">
                  English · Sahih International
                </p>
                <p className="text-[17px] leading-relaxed text-night-100">
                  <HighlightAllahText text={translationEn} />
                </p>
                {isSajdahAyah && (
                  <div className="mt-4">
                    <SajdahInlinePill />
                  </div>
                )}
              </div>
            )}
            {showUr && translationUr && (
              <div className="border-t border-night-500/15 pt-5">
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-night-300">
                  Urdu · Jalandhari
                </p>
                <p
                  lang="ur"
                  dir="rtl"
                  className="text-[24px] leading-loose text-night-50"
                >
                  <HighlightAllahText text={translationUr} />
                </p>
                {isSajdahAyah && (
                  <div className="mt-4 flex justify-end">
                    <SajdahInlinePill />
                  </div>
                )}
              </div>
            )}
            {!showEn && !showUr && (
              <p className="text-sm text-night-300">
                No translation selected. Choose English or Urdu above.
              </p>
            )}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-night-500/15 pt-6">
            <button
              type="button"
              onClick={onCopy}
              className="inline-flex items-center gap-2 rounded-lg bg-night-700 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-night-50 transition-colors hover:bg-night-600"
            >
              <IconCopy className="h-4 w-4" />
              {copyStatus === 'copied' ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={onShare}
              className="inline-flex items-center gap-2 rounded-lg bg-night-700 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-night-50 transition-colors hover:bg-night-600"
            >
              <IconShare className="h-4 w-4" />
              Share
            </button>
            <button
              type="button"
              onClick={onToggleRead}
              disabled={isToggleReadDisabled}
              title={
                isToggleReadDisabled && !isMarkedRead
                  ? 'Sign in to track your reading progress'
                  : 'Shortcut: Shift + R'
              }
              aria-pressed={isMarkedRead}
              className={cn(
                'ml-auto inline-flex items-center gap-2 rounded-lg px-5 py-2 text-xs uppercase tracking-wide transition-all',
                'disabled:cursor-not-allowed disabled:opacity-50',
                isMarkedRead
                  ? // Subdued "done" state — still cyan-tinted (so it
                    // reads as toggleable, not disabled) but visually
                    // recedes from primary-action prominence.
                    'border border-beam/20 bg-beam/5 font-medium text-beam/70 hover:border-beam/40 hover:bg-beam/10 hover:text-beam'
                  : // Unmarked: primary call-to-action.
                    'bg-beam font-semibold text-beam-ink hover:brightness-110',
              )}
            >
              <IconCheckCircle className="h-4 w-4" />
              {isMarkedRead ? 'Marked as read' : 'Mark as read'}
              {/* Kbd hint — hidden on small screens to save space (and
                  because mobile users have no keyboard anyway). */}
              <kbd
                aria-hidden="true"
                className={cn(
                  'ml-1 hidden items-center gap-0.5 rounded border px-1.5 py-0.5 font-mono text-[10px] font-medium sm:inline-flex',
                  isMarkedRead
                    ? 'border-beam/15 bg-beam/5 text-beam/50'
                    : 'border-beam-ink/30 bg-beam-ink/10 text-beam-ink/70',
                )}
              >
                ⇧R
              </kbd>
            </button>
          </div>
        </>
      )}
    </article>
  );
}

function SajdahInlinePill() {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-[linear-gradient(135deg,rgba(251,191,36,0.14),rgba(0,229,255,0.08))] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-100 shadow-[0_0_24px_rgba(251,191,36,0.08)] animate-[pulse_2.2s_ease-in-out_infinite]">
      <span className="h-2 w-2 rounded-full bg-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.55)]" />
      Sajdah | Bow Down | سجدة
    </div>
  );
}

function SajdahPrompt({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-50 flex items-start justify-center px-4 pt-24">
      <div className="pointer-events-auto w-full max-w-sm rounded-3xl border border-amber-300/30 bg-night-900/95 p-5 shadow-[0_25px_80px_rgba(4,10,28,0.55)] backdrop-blur-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-amber-200">
              Sajdah Cue
            </p>
            <h3 className="mt-2 text-lg font-extrabold text-night-50">
              Sajdah / Bow Down to Allah
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close Sajdah prompt"
            className="rounded-full border border-night-500/40 px-2.5 py-1 text-sm text-night-200 transition-colors hover:border-amber-300/40 hover:text-amber-100"
          >
            ×
          </button>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-night-200">
          This ayah is one of the verses of Sajdah. Pause, reflect, and bow down to Allah.
        </p>
      </div>
    </div>
  );
}

function AyahSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="mb-3 h-10 w-full rounded bg-night-700/60" />
      <div className="mb-10 ml-auto h-10 w-5/6 rounded bg-night-700/60" />
      <div className="space-y-2 border-t border-night-500/15 pt-5">
        <div className="h-3 w-32 rounded bg-night-700/40" />
        <div className="h-5 w-full rounded bg-night-700/40" />
        <div className="h-5 w-4/5 rounded bg-night-700/40" />
      </div>
    </div>
  );
}

function PrevNextNav({
  isFirst,
  isLast,
  prevLabel,
  nextLabel,
  onPrev,
  onNext,
}: {
  isFirst: boolean;
  isLast: boolean;
  prevLabel: string;
  nextLabel: string;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <nav aria-label="Ayah navigation" className="grid grid-cols-2 gap-4">
      <button
        type="button"
        onClick={onPrev}
        disabled={isFirst}
        className="group flex flex-col items-start gap-1 rounded-xl border border-night-500/20 bg-night-700/40 p-4 transition-colors hover:border-beam/40 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-night-500/20"
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-night-300">
          Previous
        </span>
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-night-50">
          <IconChevronRight className="h-4 w-4 rotate-180 text-beam transition-transform group-hover:-translate-x-1" />
          {prevLabel}
        </span>
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={isLast}
        className="group flex flex-col items-end gap-1 rounded-xl border border-night-500/20 bg-night-700/40 p-4 text-right transition-colors hover:border-beam/40 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-night-500/20"
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-night-300">
          Next
        </span>
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-night-50">
          {nextLabel}
          <IconChevronRight className="h-4 w-4 text-beam transition-transform group-hover:translate-x-1" />
        </span>
      </button>
    </nav>
  );
}

/* ---- Right rail ---------------------------------------------------- */

function TafsirCard({
  tafsir,
  isLoading,
  topics,
}: {
  tafsir: string | null;
  isLoading: boolean;
  topics: string[];
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const preview = truncateWords(tafsir, 20);
  const hasMoreThanPreview =
    tafsir != null && countWords(tafsir) > 20 && preview !== tafsir;

  useEffect(() => {
    setIsExpanded(false);
  }, [tafsir]);

  return (
    <div className="h-full rounded-3xl border border-night-500/30 bg-night-950/60 p-6 backdrop-blur-md md:p-7">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-extrabold text-beam">
          Summary of Tafsir
        </h3>
        <span className="text-beam">
          <IconSparkle className="h-5 w-5" />
        </span>
      </div>
      {isLoading ? (
        <div className="mt-6 animate-pulse space-y-3">
          <div className="h-4 w-full rounded bg-night-700/60" />
          <div className="h-4 w-5/6 rounded bg-night-700/60" />
          <div className="h-4 w-4/6 rounded bg-night-700/60" />
        </div>
      ) : tafsir ? (
        <div className="mt-6 space-y-5">
          <div className="space-y-3 text-[15px] leading-relaxed text-night-200">
            <p>{isExpanded ? tafsir : preview}</p>
            {hasMoreThanPreview && (
              <button
                type="button"
                onClick={() => setIsExpanded((v) => !v)}
                className="text-xs font-semibold uppercase tracking-[0.18em] text-beam transition-colors hover:text-night-50"
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </button>
            )}
          </div>
        </div>
      ) : (
        // Fallback when an ayah has no tafsir rows in the DB. The chosen
        // phrasing leans positive — many short, devotional ayahs do not
        // require commentary to be understood in plain meaning.
        <p className="mt-6 text-sm italic text-night-300">
          The verse is clear to understand.
        </p>
      )}
      {topics.length > 0 && (
        <div className="mt-8 border-t border-night-500/15 pt-6">
          <h4 className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-beam">
            Topics
          </h4>
          <div className="flex flex-wrap gap-2">
            {topics.map((t) => (
              <span
                key={t}
                className="rounded-full bg-night-500/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-night-100"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Audio player -------------------------------------------------- */

function AudioPlayer({
  audio,
  surahName,
  ayahNumber,
}: {
  audio: AudioApi;
  surahName: string | undefined;
  ayahNumber: number;
}) {
  return (
    <div
      className={cn(
        'mt-8 mx-auto w-full max-w-4xl',
        'flex items-center gap-4 sm:gap-6',
        'rounded-full border border-night-500/30 bg-night-800/80 backdrop-blur-xl',
        'shadow-[0_0_30px_rgba(0,229,255,0.08)]',
        'px-4 py-3 sm:px-6 sm:py-4',
        'lg:fixed lg:bottom-6 lg:left-1/2 lg:z-40 lg:mt-0 lg:-translate-x-1/2',
      )}
    >
      {/* Reciter */}
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-beam/15 text-beam sm:h-12 sm:w-12">
          <IconMic className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
        <div className="hidden min-w-0 sm:block">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-night-50">
            Mishary Rashid Alafasy
          </p>
          <p className="truncate text-[10px] uppercase tracking-wide text-night-300">
            {surahName ?? 'Loading…'}
            {ayahNumber ? ` · Verse ${ayahNumber}` : ''}
          </p>
        </div>
      </div>

      {/* Controls + progress */}
      <div className="flex flex-1 items-center justify-center gap-3 sm:gap-5">
        <button
          type="button"
          onClick={() => audio.seekRelative(-10)}
          disabled={!audio.duration}
          aria-label="Replay 10 seconds"
          className="text-night-200 transition-colors hover:text-beam disabled:opacity-40"
        >
          <IconReplay10 className="h-6 w-6" />
        </button>
        <button
          type="button"
          onClick={() => (audio.isPlaying ? audio.pause() : audio.play())}
          disabled={audio.isLoading && !audio.isPlaying}
          aria-label={audio.isPlaying ? 'Pause' : 'Play'}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-beam text-beam-ink shadow-[0_0_20px_rgba(0,229,255,0.3)] transition-transform hover:scale-105 active:scale-95 disabled:opacity-60 sm:h-12 sm:w-12"
        >
          {audio.isPlaying ? (
            <IconPause className="h-6 w-6" />
          ) : (
            <IconPlay className="ml-0.5 h-6 w-6" />
          )}
        </button>
        <button
          type="button"
          onClick={() => audio.seekRelative(10)}
          disabled={!audio.duration}
          aria-label="Forward 10 seconds"
          className="text-night-200 transition-colors hover:text-beam disabled:opacity-40"
        >
          <IconForward10 className="h-6 w-6" />
        </button>

        {/* Progress */}
        <div className="hidden flex-1 max-w-xs items-center gap-3 md:flex">
          <span className="text-[11px] tabular-nums text-night-300">
            {formatTime(audio.currentTime)}
          </span>
          <ProgressBar
            current={audio.currentTime}
            duration={audio.duration}
            onSeek={audio.seek}
          />
          <span className="text-[11px] tabular-nums text-night-300">
            {formatTime(audio.duration)}
          </span>
        </div>
      </div>

      {/* Right meta */}
      <div className="hidden items-center gap-3 text-night-300 sm:flex">
        {audio.error ? (
          <button
            type="button"
            onClick={audio.retry}
            className="rounded-full border border-red-400/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-red-300 hover:bg-red-400/10"
          >
            Retry
          </button>
        ) : (
          <button
            type="button"
            className="transition-colors hover:text-beam"
            aria-label="Volume"
          >
            <IconVolume className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}

function ProgressBar({
  current,
  duration,
  onSeek,
}: {
  current: number;
  duration: number;
  onSeek: (s: number) => void;
}) {
  const pct = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;
  const handleClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (duration <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    onSeek(Math.max(0, Math.min(duration, ratio * duration)));
  };
  return (
    <div
      role="slider"
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      tabIndex={0}
      onClick={handleClick}
      className="group relative h-1.5 flex-1 cursor-pointer rounded-full bg-night-500/30"
    >
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-beam transition-[width] duration-150"
        style={{ width: `${pct}%` }}
      >
        <div className="absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 translate-x-1/2 rounded-full bg-beam opacity-0 shadow-[0_0_10px_rgba(0,229,255,0.6)] transition-opacity group-hover:opacity-100" />
      </div>
    </div>
  );
}

/* ================================================================== *
 * Helpers
 * ================================================================== */

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

function formatTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function truncateWords(text: string | null, maxWords: number): string {
  if (!text) return '';
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text.trim();
  return `${words.slice(0, maxWords).join(' ')}...`;
}

function countWords(text: string | null): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
}
